import { authApi } from 'api';

/**
 * Get club age groups
 * @param {string} clubId - The club ID
 * @returns {Promise} - The response from the API
 */
export const getClubAgeGroups = async (clubId) => {
  try {
    const response = await authApi.get(`/Clubs/${clubId}/agegroups`);
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Create a new club age group
 *
 * @param {string} clubId - The club ID.
 * @param {Object} groupData - An object containing the details of the age group to be created.
 * @param {string} groupData.ageGroupName - The name of the age group.
 * @param {string} groupData.ageRange - The age range
 * @param {boolean} groupData.genderSplit - Whether the group is split by gender.
 * @param {boolean} groupData.isVisible - Whether the group is visible.
 * @param {number} groupData.sortOrder - The display order of the group.
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const createClubAgeGroup = async (clubId, groupData) => {
  try {
    const response = await authApi.post(
      `/Clubs/${clubId}/agegroups`,
      groupData
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Get club age group by Id
 *
 * @param {string} clubId - The club ID.
 * @param {string} groupId - The age group ID.
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const getClubAgeGroupById = async (clubId, groupId) => {
  try {
    const response = await authApi.get(`/Clubs/${clubId}/agegroups/${groupId}`);
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Get club age group by Id
 *
 * @param {string} clubId - The club ID.
 * @param {string} groupId - The age group ID.
 * @param {Object} groupData - An object containing the details of the age group to be created.
 * @param {string} groupData.ageGroupName - The name of the age group.
 * @param {string} groupData.ageRange - The age range
 * @param {boolean} groupData.genderSplit - Whether the group is split by gender.
 * @param {boolean} groupData.isVisible - Whether the group is visible.
 * @param {number} groupData.sortOrder - The display order of the group.
 * @returns {Promise} - A promise resolving with the API response.
 */
export const updateClubAgeGroupById = async (clubId, groupId, groupData) => {
  try {
    const response = await authApi.put(
      `/Clubs/${clubId}/agegroups/${groupId}`,
      groupData
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Get meet-program usage for an age group (admin only).
 * Returns { inUse, affectedMeets: [{ meetId, meetName, meetDate, meetStatus }],
 * completedMeetUsage: { meetCount } } — drives the edit popup's usage warnings
 * (age-group-lifecycle-design.md §5.3/§7.4).
 *
 * @param {string} clubId - The club ID.
 * @param {string} groupId - The age group ID.
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const getClubAgeGroupUsage = async (clubId, groupId) => {
  try {
    const response = await authApi.get(
      `/Clubs/${clubId}/agegroups/${groupId}/usage`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Delete (tombstone) a club age group.
 *
 * Confirm-and-report protocol (age-group-lifecycle-design.md §5.2): if the
 * group is used in the programs of non-completed meets, the first call returns
 * 409 with an impact report; resubmit with the report's meet IDs as
 * acknowledgedMeetIds to confirm. Nothing in those meets changes either way.
 *
 * @param {string} clubId - The club ID.
 * @param {string} groupId - The age group ID.
 * @param {string[]} [acknowledgedMeetIds] - Meet IDs from the 409 report being acknowledged.
 *
 * @returns {Promise} - A promise resolving with the API response.
 */
export const deleteClubAgeGroupById = async (
  clubId,
  groupId,
  acknowledgedMeetIds
) => {
  try {
    const response = await authApi.delete(
      `/Clubs/${clubId}/agegroups/${groupId}`,
      acknowledgedMeetIds && acknowledgedMeetIds.length > 0
        ? { data: { resolutions: { acknowledgedMeetIds } } }
        : undefined
    );
    return response;
  } catch (err) {
    throw err;
  }
};
